(() => {
  const requests = new Map();
  const load = (url) => {
    if (!requests.has(url)) requests.set(url, fetch(url).then((response) => {
      if (!response.ok) throw new Error('Artist content could not be loaded.');
      return response.json();
    }));
    return requests.get(url);
  };
  const data = (root, selector) => JSON.parse(root.querySelector(selector)?.textContent || '{}');
  const picture = (image, urls) => {
    const item = urls[image.filename];
    if (!item) return null;
    const img = document.createElement('img');
    img.src = item.thumb;
    img.alt = image.alt || '';
    img.loading = 'lazy';
    img.decoding = 'async';
    if (image.width && image.height) { img.width = image.width; img.height = image.height; }
    return img;
  };
  const viewer = async (content) => {
    const messages = data(content.closest('[data-artist-page]'), '[data-art-native-i18n]');
    const grid = content.querySelector('.art-artist-grid');
    if (!grid || grid.closest('art-portfolio-gallery')) return;
    await customElements.whenDefined('art-portfolio-gallery');
    const gallery = document.createElement('art-portfolio-gallery');
    gallery.dataset.batchSize = '10000';
    const more = document.createElement('button');
    more.type = 'button'; more.hidden = true; more.dataset.artMore = '';
    more.textContent = messages.more || 'Show More';
    const dialog = document.createElement('dialog');
    dialog.className = 'art-artist-dialog'; dialog.setAttribute('aria-label', messages.viewer || 'Image viewer');
    dialog.innerHTML = '<button type="button" data-art-close aria-label="Close image viewer">×</button><button type="button" data-art-previous aria-label="Previous image">‹</button><img data-art-full alt="" width="1600" height="1200"><p><span data-art-caption></span><span data-art-counter aria-live="polite"></span></p><button type="button" data-art-next aria-label="Next image">›</button>';
    for (const [selector, key] of [['[data-art-close]', 'close'], ['[data-art-previous]', 'previous'], ['[data-art-next]', 'next']]) { if (messages[key]) dialog.querySelector(selector).setAttribute('aria-label', messages[key]); }
    const parent = grid.parentNode;
    gallery.append(grid, more, dialog);
    parent.append(gallery);
  };
  // Normalize presentation only: preserve biography words, links and existing inline markup.
  function formatArtistBiography(content) {
    content.querySelectorAll('.art-artist-description').forEach(biography => {
      biography.querySelectorAll('p').forEach(paragraph => {
        if (paragraph.querySelector('br') && [...paragraph.children].every(node => node.tagName === 'BR')) {
          const lines = [];
          let line = '';
          paragraph.childNodes.forEach(node => {
            if (node.nodeName === 'BR') { if (line.trim()) lines.push(line); line = ''; }
            else line += node.textContent;
          });
          if (line.trim()) lines.push(line);
          paragraph.replaceWith(...lines.map(text => {
            const p = document.createElement('p'); p.textContent = text; return p;
          }));
        }
      });
      const walker = document.createTreeWalker(biography, NodeFilter.SHOW_TEXT);
      while (walker.nextNode()) {
        walker.currentNode.nodeValue = walker.currentNode.nodeValue.replace(/\u00a0/g, ' ').replace(/[ \t]{2,}/g, ' ');
      }
      biography.querySelectorAll('p').forEach(paragraph => {
        if (!paragraph.textContent.trim() && !paragraph.querySelector('img, video, iframe')) { paragraph.remove(); return; }
        if (!paragraph.hasAttribute('dir')) paragraph.setAttribute('dir', 'auto');
        const text = paragraph.textContent.trim();
        if (text.length < 90 && /(?:biography|personal details|education|solo exhibitions|group exhibitions|selected exhibitions|exhibitions|collections|awards|plein air):?$/i.test(text)) {
          paragraph.classList.add('art-artist-biography-heading');
        }
      });
      biography.classList.add('art-artist-description--formatted');
    });
  }
  async function init(root) {
    if (root.dataset.artistInitialized) return;
    root.dataset.artistInitialized = 'true';
    const status = root.querySelector('[data-artist-status]');
    try {
      const urls = data(root, '[data-artist-image-urls]');
      if (root.hasAttribute('data-artist-index')) {
        const manifest = await load(root.dataset.manifest);
        const links = data(root, '[data-artist-page-urls]');
        const cards = root.querySelector('[data-artist-cards]');
        const requestedOrder = data(root, '[data-artist-order]');
        const names = typeof requestedOrder === 'string'
          ? requestedOrder.split(/\r?\n/).map((name) => name.trim().toLocaleLowerCase()).filter(Boolean) : [];
        const ranks = new Map();
        names.forEach((name, index) => { if (!ranks.has(name)) ranks.set(name, index); });
        const artists = manifest.artists.map((artist, index) => ({ artist, index,
          rank: ranks.get(artist.name.toLocaleLowerCase()) ?? ranks.get(artist.handle) ?? names.length
        })).sort((a, b) => a.rank - b.rank || a.index - b.index);
        for (const { artist } of artists) {
          const link = document.createElement('a');
          link.href = links[artist.handle] && !links[artist.handle].includes('undefined') ? links[artist.handle] : '/pages/contact?view=artist&artist=' + encodeURIComponent(artist.handle);
          const image = artist.images.find((item) => urls[item.filename]);
          if (image) link.append(picture(image, urls));
          const heading = document.createElement('h2');
          heading.textContent = artist.name;
          link.append(heading); cards.append(link);
        }
      } else {
        const content = root.querySelector('[data-artist-content]');
        if (!content.textContent.trim() && !content.querySelector('img')) {
          const manifest = await load(root.dataset.manifest);
          const query = root.dataset.previewMode === 'true' ? new URLSearchParams(location.search).get('artist') : null;
          const handle = query || (root.dataset.handle === 'contact' ? root.dataset.preview : root.dataset.handle);
          const artist = manifest.artists.find((item) => item.handle === handle);
          if (!artist) throw new Error('Artist page content is not available yet.');
          root.querySelector('[data-artist-title]').textContent = artist.name;
          const biography = document.createElement('div');
          biography.className = 'art-artist-description';
          // The migration manifest contains only escaped original text with p/br markup.
          biography.innerHTML = artist.description_html;
          const grid = document.createElement('div'); grid.className = 'art-artist-grid';
          for (const image of artist.images) {
            const img = picture(image, urls);
            if (!img) continue;
            const link = document.createElement('a');
            link.href = urls[image.filename].full;
            link.dataset.artImage = ''; link.dataset.caption = image.alt || artist.name;
            link.setAttribute('aria-label', image.alt || artist.name);
            link.setAttribute('aria-haspopup', 'dialog');
            link.append(img); grid.append(link);
          }
          content.append(biography, grid);
        }
        formatArtistBiography(content);
        await viewer(content);
      }
      if (status) status.hidden = true;
    } catch (error) {
      root.dataset.artistInitialized = '';
      if (status) { status.textContent = data(root, '[data-art-native-i18n]').artist_error || error.message; status.hidden = false; }
    }
  }
  const boot = (scope = document) => scope.querySelectorAll('[data-artist-page], [data-artist-index]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
  else boot();
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();
